/**
 * PATIENT SAFETY COMPONENT — display-only. Never add data fetching here.
 * All clinical views that display patient identity should use this component.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { tokens } from '@healthdesk/shared-utils';

function normalizeAllergyLabel(a) {
  if (typeof a === 'string') return a;
  if (a && typeof a === 'object' && a.name) return a.name;
  try {
    return JSON.stringify(a);
  } catch (e) {
    console.warn('PatientBanner: could not stringify allergy entry', e);
    return String(a);
  }
}

export function PatientBanner({ patient, density = 'full' }) {
  const theme = useTheme();
  if (!patient) return null;

  const {
    name,
    dob,
    mrn,
    aka,
    allergies,
    phone,
  } = patient;

  const onClinicalChip = {
    bgcolor: tokens.clinical.high.light,
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: tokens.typography.size.caption,
  };

  if (density === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {name}
        </Typography>
        {dob && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            DOB: {dob}
          </Typography>
        )}
        {mrn && (
          <Chip label={`MRN: ${mrn}`} size="small" variant="outlined" />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: 2,
        borderRadius: `${tokens.radius.card}px`,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {name}
        </Typography>
        {mrn && (
          <Chip label={`MRN: ${mrn}`} size="small" variant="outlined" />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {dob && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            DOB: {dob}
          </Typography>
        )}
        {phone && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {phone}
          </Typography>
        )}
      </Box>

      {aka && aka.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>AKA:</Typography>
          {aka.map((akaName, i) => (
            <Chip key={i} label={akaName} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {allergies && allergies.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Allergies:
            </Typography>
            {allergies.map((a, i) => (
              <Chip
                key={i}
                label={normalizeAllergyLabel(a)}
                size="small"
                sx={onClinicalChip}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

const patientBannerDefault = PatientBanner;
export default patientBannerDefault;
